import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogoFull } from '../components/Logo';
import { authConfigured, signIn } from '../lib/auth';
import { firstName, forgetSignIn, rememberCode, rememberedSignIn } from '../lib/signInMemory';
import './Login.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * The front door, with two ways through it.
 *
 * Staff go to Microsoft. Contractors type the address NCMB holds for them and
 * get a six-digit code by email - which replaces a scheme where an
 * administrator generated a permanent token, emailed it, and the contractor
 * pasted it into a box. That token never expired and sat in the database in
 * clear, so it carried all of email's exposure and none of its expiry.
 *
 * The one thing this page must never do is say whether an address belongs to
 * a contractor. Every answer to "send me a code" is identical, so the box
 * cannot be used to read off who NCMB works with.
 *
 * A DEVICE THAT HAS BEEN HERE BEFORE (lib/signInMemory). Gordon: "it is still
 * asking me whether I use the Microsoft login or PIN even if I have accessed
 * Jarvis and closed it off multiple times per day." Somebody who last came in
 * with Microsoft is normally signed straight back in before this page is ever
 * drawn (lib/auth's quiet sign-in). When Microsoft wants them to press
 * something, this page leads with "Continue as Rita" - one press, Microsoft
 * told who to expect - and the other ways in sit under it. A contractor who
 * last came in with a code finds the address box open with their address in
 * it. Signing out on purpose forgets all of it, and the door asks again.
 */
export default function Login() {
  const navigate = useNavigate();
  const [memo, setMemo] = useState(() => rememberedSignIn());
  const returning = authConfigured && memo?.method === 'microsoft' ? memo : null;
  const [stage, setStage] = useState(() => (returning ? 'returning'
    : memo?.method === 'code' ? 'email' : 'choose'));   // returning | choose | email | code
  const [email, setEmail] = useState(() => (memo?.method === 'code' ? memo.email : ''));
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const microsoft = (opts) => {
    setBusy(true);
    setError(null);
    signIn(opts).catch(() => {
      setBusy(false);
      setError('Could not reach Microsoft. Check the connection and try again.');
    });
  };

  const askForCode = async (e) => {
    e?.preventDefault();
    setBusy(true); setError(null);
    try {
      const res = await axios.post(`${API_BASE}/auth/contractor/request-code`,
        { email: email.trim() });
      setNotice(res.data?.message
        || 'If that address belongs to a contractor, a code is on its way.');
      setStage('code');
      setCode('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start sign-in. Try again shortly.');
    } finally { setBusy(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const res = await axios.post(`${API_BASE}/auth/contractor/verify-code`,
        { email: email.trim(), code: code.trim() });
      localStorage.setItem('contractor_token', res.data.token);
      localStorage.setItem('contractor_name', res.data.contractor.name);
      localStorage.setItem('contractor_id', res.data.contractor.id);
      // Next time, the address box opens with this in it.
      rememberCode(email.trim());
      navigate('/contractor-dashboard');
    } catch (err) {
      setError(err.response?.data?.error
        || 'That code is not right, or it has expired. Ask for a new one.');
    } finally { setBusy(false); }
  };

  /** "Not you?": this device forgets them, and the door asks from the start. */
  const notMe = () => {
    forgetSignIn();
    setMemo(null);
    setEmail('');
    setError(null);
    setNotice(null);
    setStage('choose');
  };

  const greet = returning ? firstName(returning.name) : null;

  return (
    <div className="login">
      <div className="login-card">
        {/* The whole lockup here, tagline and JPA mark included. The sign-in
            page is the one screen with room to show it at a size where the
            Consulting/Auditing/Corporate line can actually be read, and it is
            the first thing anyone sees of the firm. */}
        <div className="login-brand">
          {/* No strap line under it. The artwork already carries "IT SUPPORT
              TICKETING" beneath the Jarvis wordmark, and a caption repeating
              that said the same thing twice, two lines apart. */}
          <LogoFull height={58} />
        </div>

        {error && <p className="login-error" role="alert">{error}</p>}

        {stage === 'returning' && returning && (
          <div className="login-door login-returning" data-returning="">
            <h2>{greet ? `Welcome back, ${greet}` : 'Welcome back'}</h2>
            <p>Pick up where you left off - one press and you are in.</p>
            <button type="button" className="login-btn is-primary login-continue" disabled={busy}
                    onClick={() => microsoft({ hint: returning.hint })}>
              <span className="login-continue-text">
                {busy ? 'Opening Microsoft…' : `Continue as ${returning.name || returning.hint}`}
              </span>
              {returning.name && !busy && <span className="login-continue-hint">{returning.hint}</span>}
            </button>
            <div className="login-other">
              <button type="button" className="login-back" disabled={busy}
                      onClick={() => microsoft({ pickAccount: true })}>
                Use a different Microsoft account
              </button>
              <button type="button" className="login-back" disabled={busy}
                      onClick={() => { setStage('choose'); setError(null); }}>
                Contractor? Sign in with email
              </button>
              <button type="button" className="login-back login-forget" disabled={busy} onClick={notMe}>
                Not {greet || 'you'}? Forget me on this device
              </button>
            </div>
          </div>
        )}

        {stage === 'choose' && (
          <>
            <div className="login-door">
              <h2>NCMB staff</h2>
              <p>Use the Microsoft account you sign in to Outlook with.</p>
              <button
                type="button"
                className="login-btn is-primary"
                disabled={!authConfigured || busy}
                onClick={() => microsoft({ hint: returning?.hint || null })}
              >
                Sign in with Microsoft
              </button>
              {!authConfigured && (
                <p className="login-small">
                  Microsoft sign-in is not configured on this deployment.
                </p>
              )}
              {returning && (
                <button type="button" className="login-back"
                        onClick={() => { setStage('returning'); setError(null); }}>
                  Back to {returning.name || returning.hint}
                </button>
              )}
            </div>

            <div className="login-or"><span>or</span></div>

            <div className="login-door">
              <h2>Contractors</h2>
              <p>
                We&rsquo;ll email a six-digit code to the address NCMB has for you.
                No password to remember.
              </p>
              <button type="button" className="login-btn"
                      onClick={() => { setStage('email'); setError(null); }}>
                Sign in with email
              </button>
            </div>
          </>
        )}

        {stage === 'email' && (
          <form className="login-door" onSubmit={askForCode}>
            <h2>Contractor sign-in</h2>
            <label htmlFor="login-email">Your email address</label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourcompany.com"
              required
            />
            <p className="login-small">
              It has to be the address NCMB holds for you. If you are not sure which
              one that is, ask them.
            </p>
            <button type="submit" className="login-btn is-primary" disabled={busy || !email.trim()}>
              {busy ? 'Sending…' : 'Email me a code'}
            </button>
            <button type="button" className="login-back"
                    onClick={() => { setStage(returning ? 'returning' : 'choose'); setError(null); }}>
              {memo?.method === 'code' ? 'Other ways to sign in' : 'Back'}
            </button>
          </form>
        )}

        {stage === 'code' && (
          <form className="login-door" onSubmit={submitCode}>
            <h2>Enter your code</h2>
            {notice && <p className="login-notice">{notice}</p>}
            <label htmlFor="login-code">Six-digit code</label>
            <input
              id="login-code"
              /* text rather than number: a number input strips a leading zero,
                 and one code in ten begins with one. inputMode still brings up
                 the numeric keypad on a phone. */
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              autoFocus
              className="login-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              required
            />
            <button type="submit" className="login-btn is-primary"
                    disabled={busy || code.length !== 6}>
              {busy ? 'Checking…' : 'Sign in'}
            </button>
            <button type="button" className="login-back" disabled={busy} onClick={askForCode}>
              Send another code
            </button>
            <button type="button" className="login-back"
                    onClick={() => { setStage('email'); setError(null); setNotice(null); }}>
              Use a different address
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
